import React, { useState, useEffect, useCallback } from 'react';
import { 
  CogIcon, 
  MagnifyingGlassIcon, 
  HeartIcon, 
  XMarkIcon,
  PlusIcon,
  PhotoIcon,
  PencilIcon,
  TrashIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { equipmentAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ImageUpload from '../components/ImageUpload.tsx';
import imageCacheService from '../services/imageCacheService.ts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Equipment {
  id: string;
  name: string;
  description: string;
  category: string;
  unit_of_measure: string;
  power: string;
  image_url?: string;
  customer_id: string;
  created_by: string;
  customer_name: string;
  creator_name: string;
  suppliers: Array<{
    supplier_id: string;
    supplier_name: string;
    supplier_code: string;
    base_price: number;
    currency: string;
    is_available: boolean;
  }>;
  is_favorite: boolean;
  created_at: string;
}

interface Category {
  category: string;
  count: number;
}

export const EquipmentPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, customer } = useAuth();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFavorites, setShowFavorites] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  
  // Equipment management state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    unit_of_measure: 'each',
    power: 'Petrol'
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState<Record<string, any>>({});
  
  // AI Description generation state
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  
  // Custom AI Image generation state
  const [showImagePromptModal, setShowImagePromptModal] = useState(false);
  const [customImagePrompt, setCustomImagePrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedImagePreview, setGeneratedImagePreview] = useState<string | null>(null);

  // Validation function
  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.name.trim()) {
      errors.push(t('equipment.nameRequired'));
    } else if (formData.name.length > 255) {
      errors.push(t('equipment.nameMaxLength'));
    }
    
    if (formData.description && formData.description.length > 2000) {
      errors.push(t('equipment.descriptionMaxLength'));
    }
    
    if (!formData.category.trim()) {
      errors.push(t('equipment.categoryRequired'));
    }
    
    if (formData.unit_of_measure && formData.unit_of_measure.length > 50) {
      errors.push('Unit of measure must be 50 characters or less');
    }
    
    const validPowerTypes = ['Petrol', 'Diesel', '110V', '240V', 'Battery', 'Manual', 'Hydraulic'];
    if (formData.power && !validPowerTypes.includes(formData.power)) {
      errors.push('Power type must be one of: ' + validPowerTypes.join(', '));
    }
    
    if (selectedImage && selectedImage.size > 5 * 1024 * 1024) {
      errors.push(t('equipment.imageMaxSize'));
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Check if we can generate AI description
  const canGenerateDescription = formData.name.trim() && formData.category.trim() && formData.unit_of_measure.trim() && formData.power.trim();

  // Generate AI description
  const generateAIDescription = async () => {
    if (!canGenerateDescription) return;

    try {
      setIsGeneratingDescription(true);
      
      const response = await equipmentAPI.generateDescription({
        name: formData.name,
        category: formData.category,
        unit_of_measure: formData.unit_of_measure,
        power: formData.power
      });

      if (response.data.description) {
        setFormData({ ...formData, description: response.data.description });
      }
    } catch (error: any) {
      console.error('Error generating AI description:', error);
      
      // Use fallback description if available
      if (error.response?.data?.fallback) {
        setFormData({ ...formData, description: error.response.data.fallback });
      } else {
        // Show error message
        setError('Failed to generate AI description. Please enter manually.');
      }
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Generate custom AI image
  const generateCustomImage = async () => {
    if (!customImagePrompt.trim() || !editingEquipment) return;

    try {
      setIsGeneratingImage(true);
      setError('');
      
      const response = await equipmentAPI.generateCustomImage(editingEquipment.id, {
        prompt: customImagePrompt.trim(),
        equipment_name: formData.name,
        category: formData.category,
        power: formData.power
      });

      if (response.data.image_url) {
        setGeneratedImagePreview(response.data.image_url);
      }
    } catch (error: any) {
      console.error('Error generating custom image:', error);
      setError('Failed to generate custom image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Use the generated image
  const useGeneratedImage = () => {
    if (generatedImagePreview) {
      setImagePreview(generatedImagePreview);
      setSelectedImage(null); // Clear any uploaded file
      setShowImagePromptModal(false);
      setCustomImagePrompt('');
      setGeneratedImagePreview(null);
    }
  };

  // Reset custom image generation
  const resetCustomImageGeneration = () => {
    setShowImagePromptModal(false);
    setCustomImagePrompt('');
    setGeneratedImagePreview(null);
    setIsGeneratingImage(false);
    setError('');
  };

  // Handle image selection
  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    setValidationErrors([]);
  };

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 12,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory })
      };
      
      const response = showFavorites 
        ? await equipmentAPI.getFavorites(params)
        : await equipmentAPI.getAll(params);
      
      setEquipment(response.data.equipment);
      setTotalPages(response.data.pagination.pages);
      
      // Preload images in the background
      imageCacheService.preloadEquipmentImages(response.data.equipment);
    } catch (err) {
      setError('Failed to load equipment');
      console.error('Error fetching equipment:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedCategory, showFavorites]);

  const fetchPricing = async () => {
    try {
      const response = await equipmentAPI.getPricing();
      setPricing(response.data.pricing || {});
    } catch (err) {
      console.error('Error fetching pricing:', err);
      // Don't set error state for pricing - it's optional
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await equipmentAPI.getCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchEquipment();
    fetchPricing();
  }, [currentPage, searchTerm, selectedCategory, showFavorites, fetchEquipment]);

  useEffect(() => {
    if (!showFavorites) {
      fetchCategories();
    }
  }, [showFavorites]);

  const toggleFavorite = async (equipmentId: string, isFavorite: boolean) => {
    try {
      if (isFavorite) {
        await equipmentAPI.removeFavorite(equipmentId);
      } else {
        await equipmentAPI.addFavorite(equipmentId);
      }
      
      // Update local state
      setEquipment(prev => prev.map(item => 
        item.id === equipmentId 
          ? { ...item, is_favorite: !isFavorite }
          : item
      ));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  // Handle creating hire from equipment
  const handleCreateHireFromEquipment = (equipment: Equipment, pricingData: any) => {
    // Build URL parameters for pre-filling the hire form
    const params = new URLSearchParams();
    params.set('equipment_id', equipment.id);
    params.set('equipment_name', equipment.name);
    
    if (pricingData?.supplier_id) {
      params.set('supplier_id', pricingData.supplier_id);
    }
    if (pricingData?.price_per_day) {
      params.set('price_per_day', pricingData.price_per_day.toString());
    }
    
    // Navigate to hires page with pre-filled data
    navigate(`/app/hires?${params.toString()}`);
  };



  const handleAddEquipment = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditEquipment = (equipment: Equipment) => {
    setFormData({
      name: equipment.name,
      description: equipment.description || '',
      category: equipment.category || '',
      unit_of_measure: equipment.unit_of_measure || 'each',
      power: equipment.power || 'Petrol'
    });
    setSelectedImage(null);
    setImagePreview(equipment.image_url ? equipment.image_url : null);
    setValidationErrors([]);
    setEditingEquipment(equipment);
    setShowAddModal(true);
  };

  const handleDeleteEquipment = async (equipmentId: string) => {
    if (!window.confirm(t('equipment.confirmDelete'))) {
      return;
    }

    try {
      await equipmentAPI.delete(equipmentId);
      // Refresh the equipment list after successful deletion
      await fetchEquipment();
    } catch (err) {
      console.error('Error deleting equipment:', err);
      
      // Check if it's a 404 error (equipment not found)
      if (err.response?.status === 404) {
        // Equipment was already deleted, just refresh the list
        await fetchEquipment();
        return;
      }
      
      // For other errors, show the error message
      alert('Failed to delete equipment');
    }
  };

  const handleSubmitEquipment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        ...(selectedImage && { image: selectedImage })
      };
      
      if (editingEquipment) {
        await equipmentAPI.update(editingEquipment.id, submitData);
      } else {
        await equipmentAPI.create(submitData);
      }
      
      setShowAddModal(false);
      resetForm();
      await fetchEquipment();
    } catch (err: any) {
      console.error('Error saving equipment:', err);
      if (err.response?.data?.details) {
        setValidationErrors(err.response.data.details);
      } else {
        alert('Failed to save equipment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form function
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      unit_of_measure: 'each',
      power: 'Petrol'
    });
    setSelectedImage(null);
    setImagePreview(null);
    setValidationErrors([]);
    setEditingEquipment(null);
    resetCustomImageGeneration();
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <CogIcon className="h-8 w-8 text-primary-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">{t('equipment.title')}</h1>
        </div>
        
        <div className="flex items-center space-x-3">
          {isAdmin && (
            <button
              onClick={handleAddEquipment}
              className="flex items-center btn btn-primary"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
{t('equipment.addEquipment')}
            </button>
          )}
          
          <button
            onClick={() => setShowFavorites(!showFavorites)}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              showFavorites
                ? 'btn btn-secondary'
                : 'btn btn-outline'
            }`}
          >
            <HeartIcon className="h-5 w-5 mr-2" />
{showFavorites ? t('equipment.showAll') : t('equipment.myFavorites')}
          </button>
        </div>
      </div>

      {/* Filters */}
      {!showFavorites && (
        <div className="card">
          <div className="card-body">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('equipment.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full input"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="lg:w-64">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full select"
                >
                  <option value="">{t('equipment.allCategories')}</option>
                  {categories.map((cat) => (
                    <option key={cat.category} value={cat.category}>
                      {cat.category} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              {(searchTerm || selectedCategory) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 mr-1" />
{t('common.clear')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Equipment Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="card-body">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded mb-4"></div>
                <div className="h-6 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="card">
          <div className="card-body text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchEquipment}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : equipment.length === 0 ? (
        <div className="card">
          <div className="card-body text-center">
            <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
{showFavorites ? t('equipment.noFavoritesYet') : t('equipment.noEquipmentFound')}
            </h3>
            <p className="text-gray-600">
              {showFavorites 
                ? t('equipment.startAddingFavorites')
                : t('equipment.tryAdjustingSearch')
              }
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">


            
            {equipment.map((item) => (
              
              <div key={item.id} className="card hover:shadow-lg transition-shadow">
                <div className="card-body">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 line-clamp-2 h-12">{item.name}</h3>
                     
                    </div>
                    <div className="flex items-center space-x-2">
                      {isAdmin && item.customer_id === customer?.id && (
                        <>
                          <button
                            onClick={() => handleEditEquipment(item)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title={t('equipment.editEquipment')}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEquipment(item.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('equipment.deleteEquipment')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleFavorite(item.id, item.is_favorite)}
                        className="flex-shrink-0"
                      >
                        {item.is_favorite ? (
                          <HeartSolidIcon className="h-5 w-5 text-red-500" />
                        ) : (
                          <HeartIcon className="h-5 w-5 text-gray-400 hover:text-red-500" />
                        )}
                      </button>
                    </div>
                  </div>



                  {/* Equipment Image */}
                  <div className="mb-4">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-32 object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300 ${item.image_url ? 'hidden' : ''}`}>
                      <PhotoIcon className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-xs text-gray-500 text-center px-2">Awaiting Photo</span>
                    </div>
                  </div>
                  
                 

                  {/* Description and Tags Section */}
                  <div className="mb-3">
                    {item.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2 h-10">{item.description}</p>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      {item.category && (
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded">
                          {item.category}
                        </span>
                      )}
                      {item.power && (
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {item.power}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    
                    {/* Community Pricing - Always reserve space */}
                    <div className="mt-3 h-18">
                      {pricing[item.id] ? (
                        <button 
                          onClick={() => handleCreateHireFromEquipment(item, pricing[item.id])}
                          className="w-full bg-green-50 border border-green-200 rounded-lg p-3 hover:bg-green-100 hover:border-green-300 transition-colors cursor-pointer text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-green-800">
                                Latest Price
                              </div>
                              <div className="text-lg font-bold text-green-900">
                                £{pricing[item.id].price_per_day.toFixed(2)}/day
                              </div>
                             
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-green-600">
                                by {pricing[item.id].supplier_name}
                              </div>
                              <div className="text-xs text-green-500">
                                {new Date(pricing[item.id].hire_start_date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleCreateHireFromEquipment(item, null)}
                          className="w-full h-18 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center text-gray-500"
                        >
                          <span className="text-sm">Click to create hire</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      currentPage === i + 1
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Equipment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
{editingEquipment ? t('equipment.editEquipment') : t('equipment.addEquipment')}
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitEquipment} className="space-y-6">
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <ul className="text-sm text-red-600 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Equipment Name - Full Width */}
              <div>
                <label className="label">
{t('equipment.name')} *
                </label>
                <input
                  type="text"
                  placeholder={t('equipment.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full input"
                  maxLength={255}
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  {formData.name.length}/255 characters
                </div>
              </div>

              {/* Three Column Layout for Category, Unit of Measure, and Power */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">
{t('equipment.category')} *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full select"
                    required
                  >
                    <option value="">{t('equipment.selectCategory')}</option>
                    <option value="Access Equipment">Access Equipment</option>
                    <option value="Aircon & Cooling">Aircon & Cooling</option>
                    <option value="Breaking & Drilling">Breaking & Drilling</option>
                    <option value="Painting & Decorating">Painting & Decorating</option>
                    <option value="Building & Sitework">Building & Sitework</option>
                    <option value="Cleaning">Cleaning</option>
                    <option value="Concreting & Compaction">Concreting & Compaction</option>
                    <option value="Decorating Tools & Equipment">Decorating Tools & Equipment</option>
                    <option value="Heating">Heating</option>
                    <option value="Lift & Shift">Lift & Shift</option>
                    <option value="Lighting">Lighting</option>
                    <option value="Outdoor & Gardening">Outdoor & Gardening</option>
                    <option value="Plumbing & Pipework">Plumbing & Pipework</option>
                    <option value="Power">Power</option>
                    <option value="Power Access">Power Access</option>
                    <option value="Pumping & Drying">Pumping & Drying</option>
                    <option value="Sanding & Fixing">Sanding & Fixing</option>
                    <option value="Sawing & Cutting">Sawing & Cutting</option>
                    <option value="Welding">Welding</option>
                  </select>
                </div>

                <div>
                  <label className="label">
{t('equipment.unitOfMeasure')}
                  </label>
                  <select
                    value={formData.unit_of_measure}
                    onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                    className="w-full select"
                  >
                    <option value="each">Each</option>
                    <option value="per_day">Per Day</option>
                    <option value="per_week">Per Week</option>
                    <option value="per_month">Per Month</option>
                    <option value="per_hour">Per Hour</option>
                  </select>
                </div>

                <div>
                  <label className="label">
{t('equipment.power')}
                  </label>
                  <select
                    value={formData.power}
                    onChange={(e) => setFormData({ ...formData, power: e.target.value })}
                    className="w-full select"
                  >
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="110V">110V</option>
                    <option value="240V">240V</option>
                    <option value="Battery">Battery</option>
                    <option value="Manual">Manual</option>
                    <option value="Hydraulic">Hydraulic</option>
                  </select>
                </div>
              </div>

              {/* Description - Full Width with AI Generation */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">
                    {t('equipment.description')}
                  </label>
                  <button
                    type="button"
                    onClick={generateAIDescription}
                    disabled={!canGenerateDescription || isGeneratingDescription}
                    className="btn btn-sm btn-outline btn-primary"
                  >
                    {isGeneratingDescription ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        Generate AI Description
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full textarea"
                  rows={3}
                  maxLength={2000}
                  placeholder="Enter description or click 'Generate AI Description' to auto-generate..."
                />
                <div className="text-xs text-gray-500 mt-1">
                  {formData.description.length}/2000 characters
                </div>
              </div>

              {/* Image Upload Section - Side by Side Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipment Image
                  </label>
                  <ImageUpload
                    onImageSelect={handleImageSelect}
                    currentImage={imagePreview}
                    validationErrors={validationErrors.filter(error => 
                      error.toLowerCase().includes('image') || 
                      error.toLowerCase().includes('file')
                    )}
                  />
                </div>
                
                {/* Image Preview/Current Image Display */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Current Image
                    </label>
                    {editingEquipment && (
                      <button
                        type="button"
                        onClick={() => setShowImagePromptModal(true)}
                        className="flex items-center px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                      >
                        <SparklesIcon className="h-4 w-4 mr-1" />
                        Generate New Image
                      </button>
                    )}
                  </div>
                  <div className="border border-gray-300 rounded-lg p-4 h-full min-h-[200px] flex items-center justify-center bg-gray-50">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Equipment preview"
                        className="max-w-full max-h-48 object-contain rounded-lg"
                      />
                    ) : (
                      <div className="text-gray-400 text-center">
                        <PhotoIcon className="h-12 w-12 mx-auto mb-2" />
                        <p className="text-sm">No image selected</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-soft"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary"
                >
                  {submitting ? 'Saving...' : (editingEquipment ? 'Update' : 'Add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Image Prompt Modal */}
      {showImagePromptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <SparklesIcon className="h-6 w-6 mr-2 text-purple-600" />
                Generate Custom Image
              </h2>
              <button
                onClick={resetCustomImageGeneration}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Equipment Context */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Equipment Context:</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Name:</strong> {formData.name}</p>
                  <p><strong>Category:</strong> {formData.category}</p>
                  <p><strong>Power:</strong> {formData.power}</p>
                </div>
              </div>

              {/* Custom Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe the improved image you would like:
                </label>
                <textarea
                  value={customImagePrompt}
                  onChange={(e) => setCustomImagePrompt(e.target.value)}
                  className="w-full textarea"
                  rows={4}
                  placeholder="e.g., 'A professional construction worker using a modern orange hedge trimmer in a well-lit construction site with safety equipment visible'"
                  maxLength={500}
                />
                <div className="text-xs text-gray-500 mt-1">
                  {customImagePrompt.length}/500 characters
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  💡 <strong>Tips:</strong> Be specific about the setting, lighting, worker appearance, and equipment details for best results.
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Generated Image Preview */}
              {generatedImagePreview && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Generated Image Preview:</h3>
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <img
                      src={generatedImagePreview}
                      alt="Generated equipment image"
                      className="max-w-full max-h-64 object-contain rounded-lg mx-auto"
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetCustomImageGeneration}
                  className="btn btn-soft"
                  disabled={isGeneratingImage}
                >
                  Cancel
                </button>
                
                {!generatedImagePreview ? (
                  <button
                    type="button"
                    onClick={generateCustomImage}
                    disabled={!customImagePrompt.trim() || isGeneratingImage}
                    className="btn btn-primary"
                  >
                    {isGeneratingImage ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-4 w-4 mr-2" />
                        Generate Image
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={useGeneratedImage}
                    className="btn btn-primary"
                  >
                    Use This Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
